import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';
import classNames from 'classnames';
import {
    AnimatePresence,
    motion,
    usePresence,
} from 'framer-motion';

import * as cssClassNames from '../../styles/screens/product/IntroV2.module.scss';

import {
    getMediaDuration,
    hasTextToDisplay,
    MediaType,
    MultiLangText,
    secToMs,
    setAnimationTimeout,
} from '../../shared';
import {
    SMOOTH_DURATION_S,
    SMOOTH_EASE,
    SMOOTH_EXIT_DURATION_S,
} from '../../constants/transition';
import {
    BaseChapterProps,
} from '../../components/ChapterRenderer';
import CodificationWithTranslations, {
    calculateDuration as calculateCodificationDuration,
    calculateExitDuration as calculateExitCodificationDuration,
    TranslationCodificationProps,
} from '../../components/Codification/CodificationWithTranslations';
import MediaRenderer from '../../components/MediaRenderer';


const bigCellLengthRef = 80 / 1920;
const primaryMediaHeightRef = 1040 / 1920;
const mediaWidthRef = 400 / 1080;

const codificationProps: TranslationCodificationProps = {
    characterSwitchAmount: 4,
    characterNextTrigger: 2,
    timingConfig: {
        control: 'character',
        duration: 0.03,
    },
};
const languageTransitionDelay = 0.25;
const primaryMediaEntryDuration = SMOOTH_DURATION_S;
const primaryMediaExitDuration = SMOOTH_EXIT_DURATION_S;
const secondaryMediaEntryDuration = SMOOTH_DURATION_S * 0.75;
const secondaryMediaExitDuration = SMOOTH_EXIT_DURATION_S * 0.75;
const secondaryMediaDelay = 0.35;
const pause = 4;
const smoothTransition = {
    duration: primaryMediaEntryDuration,
    ease: SMOOTH_EASE,
};
const smoothExitTransition = {
    duration: primaryMediaExitDuration,
    ease: SMOOTH_EASE,
};

const driftSpeed = 13 / 1920; // px/s
const driftDuration = 16; // s
const driftDistanceRef = driftSpeed * driftDuration;


export type IntroV2Props = BaseChapterProps & {
    headVariant?: 'text' | 'media',
    text?: MultiLangText,
    headMedia?: MediaType,
    primaryMedia?: MediaType,
    secondaryMedia?: MediaType,
};

export default function IntroV2(props: IntroV2Props) {
    const {
        headVariant = 'text',
        text,
        headMedia,
        languages,
        width,
        height,
        primaryMedia,
        secondaryMedia,
        onDurationsCalculated,
    } = props;

    const bigCellLength = bigCellLengthRef * height;
    const cols = Math.ceil(width * mediaWidthRef / bigCellLength);
    const primaryMediaHeight = Math.ceil(height * primaryMediaHeightRef / bigCellLength) * bigCellLength;
    const secondaryMediaSize = bigCellLength * cols;
    const driftDistance = driftDistanceRef * height;

    const hasText = useMemo(() => headVariant === 'text' && text && hasTextToDisplay(text, languages), [text, languages]);
    const [rewrappedText, setRewrappedText] = useState<null | MultiLangText>(hasText ? null : {});
    const [langIndex, setLangIndex] = useState(0);
    const [nextLangIndex, setNextLangIndex] = useState(0);
    const [toShowMedia, setToShowMedia] = useState(!hasText);
    const [toShowSecondaryMedia, setToShowSecondaryMedia] = useState(false);
    const [isPresent, safeToRemove] = usePresence();

    useEffect(() => {
        if (nextLangIndex === langIndex || !hasText) {
            return;
        }

        return setAnimationTimeout(() => {
            setLangIndex(nextLangIndex);
        }, secToMs(pause));
    }, [nextLangIndex]);

    useEffect(() => {
        if (!toShowMedia) {
            return;
        }

        return setAnimationTimeout(() => {
            setToShowSecondaryMedia(true);
        }, secToMs(secondaryMediaDelay));
    }, [toShowMedia]);

    useEffect(() => {
        if (!onDurationsCalculated || !rewrappedText) {
            return;
        }

        const newData = {
            ...props,
            text: rewrappedText
        };
        const duration = calculateDuration(newData);
        const exitDuration = calculateExitDuration(newData);
        onDurationsCalculated({
            duration,
            exitDuration,
        });
    }, [rewrappedText]);

    const topComponent = useMemo(() => {
        switch (headVariant) {
            case 'text':
                return hasText ? (
                    <CodificationWithTranslations
                        text={text}
                        languages={languages}
                        langIndex={langIndex}
                        codificationProps={codificationProps}
                        runFinalTextAnimation
                        languageTransitionDelay={languageTransitionDelay}
                        onTyped={() => {
                            primaryMedia ? setToShowMedia(true) : setToShowSecondaryMedia(true);
                            setNextLangIndex(i => {
                                return (i >= languages.length - 1) ? i : (i + 1);
                            });
                        }}
                        onTextRewrapped={lines => setRewrappedText(lines)}
                    />
                ) : null;

            case 'media':
                return (
                    <MediaRenderer
                        media={headMedia}
                        cover='fit'
                        mediaStyle={{
                            width: 'unset',
                        }}
                    />
                );

            default:
                return null;
        }
    }, [headVariant, text, headMedia, languages, langIndex]);

    const mediaVisible = Boolean(
        (primaryMedia && toShowMedia) || (secondaryMedia && toShowSecondaryMedia)
    );
    const exitOwnedByMedia = mediaVisible;

    useEffect(() => {
        if (isPresent || hasText || exitOwnedByMedia) {
            return;
        }

        safeToRemove();
    }, [hasText, isPresent, exitOwnedByMedia]);

    const onMediaExitComplete = isPresent ? undefined : safeToRemove;

    return (
        <div className={cssClassNames.intro_v2}>
            <AnimatePresence onExitComplete={primaryMedia && toShowMedia ? onMediaExitComplete : undefined}>
                {isPresent && primaryMedia && toShowMedia ? (
                    <motion.div
                        key="primary-media"
                        className={cssClassNames.primary_media_container}
                        style={{
                            width: `${width - bigCellLength * (cols - 1) + 1}px`,
                            height: `${primaryMediaHeight}px`,
                            bottom: `${bigCellLength * 3}px`,
                            overflow: 'hidden',
                        }}
                        initial={{ y: '100%' }}
                        animate={{
                            y: '0%',
                            transition: smoothTransition,
                        }}
                        exit={{
                            y: '-100%',
                            transition: smoothExitTransition,
                        }}
                    >
                        <motion.div
                            style={{
                                height: `calc(100% + ${driftDistance}px)`,
                            }}
                            animate={{
                                y: ['0px', `-${driftDistance}px`],
                                transition: {
                                    duration: driftDuration,
                                    ease: 'linear',
                                    repeatType: 'mirror',
                                    repeat: Infinity,
                                }
                            }}
                            exit={{
                                y: `-${driftDistance}px`,
                                transition: smoothExitTransition,
                            }}
                        >
                            <MediaRenderer
                                media={primaryMedia}
                            />
                        </motion.div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
            <AnimatePresence onExitComplete={
                (!primaryMedia || !toShowMedia) && secondaryMedia && toShowSecondaryMedia
                    ? onMediaExitComplete
                    : undefined
            }>
                {isPresent && secondaryMedia && toShowSecondaryMedia ? (
                    <motion.div
                        key="secondary-media"
                        className={cssClassNames.secondary_media_container}
                        style={{
                            height: `${secondaryMediaSize}px`,
                            width: `${secondaryMediaSize}px`,
                            bottom: `${bigCellLength * 5}px`,
                        }}
                        initial={{
                            clipPath: 'inset(0% 0% 100% 0%)'
                        }}
                        animate={{
                            clipPath: 'inset(0% 0% 0% 0%)',
                            transition: {
                                duration: secondaryMediaEntryDuration,
                                ease: SMOOTH_EASE,
                            },
                        }}
                        exit={{
                            clipPath: 'inset(100% 0% 0% 0%)',
                            transition: {
                                duration: secondaryMediaExitDuration,
                                ease: SMOOTH_EASE,
                            },
                        }}
                    >
                        <MediaRenderer
                            media={secondaryMedia}
                        />
                    </motion.div>
                ) : null}
            </AnimatePresence>
            {topComponent ? (
                <div className={classNames(cssClassNames.top_container, {
                    [cssClassNames.text_container]: headVariant === 'text' && hasText,
                    [cssClassNames.top_media_container]: headVariant === 'media' && headMedia,
                })}>
                    <div>
                        <AnimatePresence onExitComplete={isPresent || exitOwnedByMedia ? undefined : safeToRemove}>
                            {isPresent ? topComponent : null}
                        </AnimatePresence>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function calculateDuration(data: IntroV2Props) {
    const {
        text,
        languages,
        primaryMedia,
        secondaryMedia,
    } = data;
    const entryDuration = 0;

    const primaryMediaDuration = primaryMedia ? getMediaDuration(primaryMedia) : 0;
    const secondaryMediaDuration = secondaryMedia ? getMediaDuration(secondaryMedia) : 0;
    const mediaDuration = Math.max(primaryMediaDuration, secondaryMediaDuration) || pause;

    const hasText = text && hasTextToDisplay(text, languages);
    const textDuration = hasText ? calculateCodificationDuration({
        text,
        languages,
        codificationProps,
        languageTransitionDelay,
    }): 0;
    const totalTextDuration = textDuration +  pause * (hasText ? languages.length : 1);

    const mainDuration = primaryMedia || secondaryMedia ? mediaDuration : totalTextDuration;

    const total = entryDuration + mainDuration;

    const duration = secToMs(total) + calculateExitDuration(data);

    return duration;
}

export function calculateExitDuration(data: IntroV2Props) {
    const {
        text,
        languages,
        primaryMedia,
        secondaryMedia,
    } = data;

    const hasText = text && hasTextToDisplay(text, languages);
    const textExitDuration = hasText ? calculateExitCodificationDuration({
        text,
        languages,
        codificationProps,
        languageTransitionDelay,
    }) : 0;

    const mediaExitDuration = primaryMedia
        ? primaryMediaExitDuration
        : (secondaryMedia ? secondaryMediaExitDuration : 0);

    return secToMs(Math.max(mediaExitDuration, textExitDuration));
}
